<template>
    <sidebar @close="$emit('close')">
        <template v-slot:title v-if="earning">
            {{ earning.name }}
        </template>

        <template v-slot:header v-if="earning">
            <div class="row mb-0">
                <div class="col">
                    <span class="text-muted">
                        {{ earning.company.name }}
                    </span>
                </div>
            </div>
            <!-- Tabs -->
            <div class="nav-tabs-container">
                <div class="nav-tabs-scroll-helper">
                    <ul class="nav nav-tabs">
                        <li class="nav-item">
                            <a class="nav-link" :class="{active: activeTab == 'main'}" href="#tabEarningContentMain" id="tabEarningLinkMain" data-toggle="tab" role="tab" aria-controls="tabEarningContentMain" :aria-selected="activeTab == 'main'">Main</a>
                        </li>
                    </ul>
                </div>
            </div>
        </template>

        <slot>
            <div class="tab-content" v-if="earning">
                <div class="tab-pane" :class="{'show active': activeTab == 'main'}" id="tabEarningContentMain" role="tabpanel" aria-labelledby="tabEarningLinkMain">
                    <!-- Earning type details widget -->
                    <info-widget :collapsible="false">
                        <template v-slot:title>Earning Type</template>

                        <template v-slot:headerbutton>
                            <button type="button" class="btn btn-outline-primary" @click.prevent="editEarningType">
                                <i class="far fa-pen" aria-hidden="true" title="Edit"></i>
                                Edit
                            </button>
                            <button v-if="earning.is_active" type="button" class="btn btn-outline-primary ml-1" @click.prevent="deactivate()">
                                Deactivate
                            </button>
                            <button v-else type="button" class="btn btn-outline-primary ml-1" @click.prevent="reactivate()">
                                Reactivate
                            </button>
                        </template>

                        <slot>
                            <div class="p-2">
                                <div>
                                    <span class="text-semibold">Type: </span>
                                    <span>{{ combinedTypePretty }}</span>
                                </div>
                                <div>
                                    <span class="text-semibold">Is Work Suffered: </span>
                                    <span>{{ combinedIsWorkSuffered ? 'Yes' : 'No' }}</span>
                                </div>
                                <div>
                                    <span class="text-semibold">Supplemental Treatment: </span>
                                    <span>{{ combinedSupplementalTreatment ? 'Yes' : 'No' }}</span>
                                </div>
                                <div>
                                    <span class="text-semibold">Is Virtual: </span>
                                    <span>{{ combinedIsVirtual ? 'Yes' : 'No' }}</span>
                                </div>
                                <div>
                                    <span class="text-semibold">Is Tips: </span>
                                    <span>{{ combinedIsTips ? 'Yes' : 'No' }}</span>
                                </div>

                                <div v-if="combinedIsTips">
                                    <span class="text-semibold">Is Cash Tips: </span>
                                    <span>{{ combinedIsCashTips ? 'Yes' : 'No' }}</span>
                                </div>

                                <div>
                                    <span class="text-semibold">Is Tied to Base Rate: </span>
                                    <span>{{ combinedIsTiedToBaseRate ? 'Yes' : 'No' }}</span>
                                </div>

                                <div v-if="combinedIsTiedToBaseRate">
                                    <span class="text-semibold">Base Rate Multiplier: </span>
                                    <span>{{ combinedBaseRateMultiplier }}x</span>
                                </div>

                                <div v-if="combinedIsTiedToBaseRate">
                                    <span class="text-semibold">Base Rate Addition: </span>
                                    <span>${{ $filters.formatCurrency(combinedBaseRateAddition) }}</span>
                                </div>
                            </div>
                        </slot>
                    </info-widget>

                    <!-- Positions widget -->
                    <info-widget :collapsible="false">
                        <template v-slot:title>Positions ({{ earning.num_positions }})</template>

                        <template v-slot:headerbutton>
                            <button type="button" class="btn btn-outline-primary" @click.prevent="editPositions">
                                <i class="far fa-pen" aria-hidden="true" title="Edit"></i>
                                Edit
                            </button>
                        </template>

                        <slot>
                            <div class="info-widget-empty p-2" v-if="earning.default_for_all_positions">
                                Available to all positions at this company.
                                <template v-if="earning.always_show_on_paygrid">Shown on the paygrid by default.</template>
                                <template v-else>Not shown on the paygrid by default.</template>
                            </div>
                            <div v-else-if="activePositionRels.length" class="p-1">
                                <table class="table css-table widget-table table-fixed">
                                    <thead>
                                        <tr>
                                            <th>Position</th>
                                            <th class="cw-6">Default</th>
                                        </tr>
                                    </thead>
                                    <tbody v-for="rel in activePositionRels" :key="rel.id">
                                        <tr>
                                            <td class="truncate-wrapper">
                                                <span>{{ rel.position.terminal_value }}</span>
                                            </td>
                                            <td class="cw-6">
                                                <i v-if="rel.is_default" class="text-muted fas fa-fw fa-check" aria-label="Yes"></i>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div class="info-widget-empty p-2" v-else>
                                This earning is not associated with any positions.
                            </div>
                        </slot>
                    </info-widget>
                </div>
            </div>
        </slot>

        <edit-earning-positions-modal v-if="earning && editPositionsModalOpen" ref="editEarningPositionsModal" :earning="earning" :client="client" @updated="onUpdate" @close="editPositionsModalOpen = false" />
        <edit-earning-type-modal v-if="earning && editEarningTypeModalOpen" :earning="earning" :client="client" @updated="onUpdate" @close="editEarningTypeModalOpen = false" />
    </sidebar>
</template>

<script>
import Sidebar from '@/components/Sidebar'
import InfoWidget from '@/components/InfoWidget'
import ButtonRow from '@/components/ButtonRow'
import EditEarningPositionsModal from './EditEarningPositionsModal'
import EditEarningTypeModal from './CompanyEarningTypeModal'

export default {
    props: ['earningId', 'client', 'tab', ],
    components: {Sidebar, InfoWidget, ButtonRow, EditEarningPositionsModal, EditEarningTypeModal},
    computed: {
        activePositionRels() {
            let ret = []
            if (this.earning?.position_rels) {
                ret = this.earning.position_rels.filter(r => r.position.is_active)
            }
            return ret
        },
        combinedTypePretty() {
            if (!this.earning) return ''
            if (this.earning.earning_type.fields_to_customize.includes('type')) {
                return this.earning.type_pretty
            }
            return this.earning.earning_type.type_pretty
        },
        combinedIsWorkSuffered() { return this._combinedField('is_work_suffered') },
        combinedSupplementalTreatment() { return this._combinedField('symmetry_wage_type_supplemental_treatment') },
        combinedIsVirtual() { return this._combinedField('is_virtual') },
        combinedIsTips() { return this._combinedField('is_tips') },
        combinedIsCashTips() { return this._combinedField('is_cash_tips') },
        combinedIsTiedToBaseRate() { return this.earning?.is_tied_to_base_rate },
        combinedBaseRateMultiplier() { return this.earning?.base_rate_multiplier },
        combinedBaseRateAddition() { return this.earning?.base_rate_addition },
    },
    data() {

        return {
            earning: null,
            activeTab: this.tab || 'main',
            editPositionsModalOpen: false,
            editEarningTypeModalOpen: false,
        }
    },
    watch: {
        earningId() {
            this.getEarning()
        }
    },
    mounted() {
        this.getEarning()
    },
    methods: {
        _combinedField(fieldName) {
            if (!this.earning) return null
            if (this.earning.earning_type.fields_to_customize.includes(fieldName)) {
                return this.earning[fieldName]
            }
            return this.earning.earning_type[fieldName]
        },
        getEarning() {
            this.earning = null
            this.$store.dispatch('START_LOADING')
            this.$api.get(`/clients/${this.client.id}/settings/earning-types/${this.earningId}`).then(resp => {
                this.earning = resp
                this.$store.dispatch('STOP_LOADING')
            }).catch((errors) => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showError(errors.__all__)
            })
        },
        editEarningType() {
            this.editEarningTypeModalOpen = true
        },
        editPositions() {
            this.editPositionsModalOpen = true
        },
        deactivate() {
            if (!confirm('Are you sure you want to deactivate this earning?')) {return}
            this.setStatus('inactive')
        },
        reactivate() {
            if (!confirm('Are you sure you want to reactivate this earning?')) {return}
            this.setStatus('active')
        },
        setStatus(status) {
            this.$store.dispatch('START_LOADING')
            this.$api.patch(`/clients/${this.client.id}/settings/earning-types/${this.earning.id}`, {status}).then(resp => {
                this.$store.dispatch('STOP_LOADING')
                this.onUpdate(resp)
            }).catch(errors => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showError(errors.__all__)
            })
        },
        onUpdate(earning) {
            if (earning) {
                Object.assign(this.earning, earning)
            }
            this.$emit('updated')
        }
    },
}
</script>
